import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Pencil,
    X,
    LayoutGrid,
    ImagePlus
} from 'lucide-react';
import { API } from '../../config';

const AdminServices = () => {
    const [services, setServices] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        features: '', // Will be converted to array on submit
        order: 0
    });

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/services`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setServices(response.data);
        } catch (error) {
            toast.error('Failed to fetch services');
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const data = new FormData();
            data.append('title', formData.title);
            data.append('description', formData.description);
            data.append('order', formData.order);

            // Handle features array
            const featureArray = formData.features.split('\n').filter(f => f.trim() !== '');
            data.append('features', JSON.stringify(featureArray));

            if (imageFile) {
                data.append('image', imageFile);
            }

            if (editingService) {
                await axios.put(`${API}/services/${editingService.id}`, data, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Service updated!');
            } else {
                await axios.post(`${API}/services`, data, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Service added!');
            }
            resetForm();
            fetchServices();
        } catch (error) {
            toast.error('Failed to save service');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setEditingService(null);
        setFormData({ title: '', description: '', features: '', order: 0 });
        setImageFile(null);
        setImagePreview(null);
    };

    const handleEdit = (service) => {
        setEditingService(service);
        setFormData({
            title: service.title,
            description: service.description,
            features: service.features.join('\n'),
            order: service.order || 0
        });
        setImagePreview(service.image_url);
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this service?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/services/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Service deleted!');
            fetchServices();
        } catch (error) {
            toast.error('Failed to delete service');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Our Services</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage core services offered by SR Agro Farms</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm flex items-center font-medium text-sm"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Service
                </motion.button>
            </div>

            {/* Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {editingService ? 'Edit Service' : 'Add New Service'}
                                </h3>
                                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Title</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                            placeholder="e.g. Broiler Parenting"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            rows="3"
                                            required
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                            placeholder="Briefly describe the service..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Features / Points (One per line)</label>
                                        <textarea
                                            rows="5"
                                            value={formData.features}
                                            onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                            placeholder="• Superior quality eggs&#10;• Ethical farming&#10;• 24/7 Vet support"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Image</label>
                                        <div className="relative group aspect-video bg-white rounded-2xl border-2 border-dashed border-gray-300 overflow-hidden flex items-center justify-center">
                                            {imagePreview ? (
                                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="text-center text-gray-400">
                                                    <ImagePlus className="h-10 w-10 mx-auto mb-2 opacity-20" />
                                                    <p className="text-sm">Click to upload image</p>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                                        <input
                                            type="number"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="flex-1 bg-gray-900 text-white px-8 py-3 rounded-xl font-medium disabled:opacity-50"
                                        >
                                            {loading ? 'Saving...' : 'Save Service'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-8 py-3 rounded-xl border border-gray-300 font-medium"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map((service) => (
                    <div key={service.id} className="bg-gray-50 rounded-3xl overflow-hidden border border-gray-100 group">
                        <div className="aspect-video relative overflow-hidden bg-gray-200">
                            {service.image_url && (
                                <img src={service.image_url} alt={service.title} className="w-full h-full object-cover" />
                            )}
                            <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEdit(service)} className="bg-white/90 backdrop-blur p-2 rounded-lg text-gray-900 hover:bg-white shadow-lg">
                                    <Pencil className="h-5 w-5" />
                                </button>
                                <button onClick={() => handleDelete(service.id)} className="bg-white/90 backdrop-blur p-2 rounded-lg text-red-600 hover:bg-white shadow-lg">
                                    <Trash2 className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                            <p className="text-gray-500 text-sm line-clamp-2 mb-4">{service.description}</p>
                            <div className="flex flex-wrap gap-2">
                                {service.features.slice(0, 3).map((f, i) => (
                                    <span key={i} className="text-[10px] uppercase tracking-wider font-bold text-agro-green-dark bg-agro-lime/20 px-2 py-1 rounded">
                                        {f}
                                    </span>
                                ))}
                                {service.features.length > 3 && (
                                    <span className="text-[10px] text-gray-400 font-bold px-2 py-1">+{service.features.length - 3} more</span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {services.length === 0 && (
                    <div className="col-span-full py-16 text-center text-gray-400 flex flex-col items-center">
                        <LayoutGrid className="h-12 w-12 mb-3 opacity-20" />
                        <p>No services configured yet</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AdminServices;
